import { useEffect, useState } from "react";

import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Select from "../../../components/common/Select";

import { calculateItemTotal } from "../utils/offer.utils";

const OfferItemForm = ({
  index,
  value = {},
  onChange,
  onRemove,
  canRemove,
  errors = {},
  offeringOptions = [],
  fetchPricingPlans,
}) => {
  const [pricingPlans, setPricingPlans] =
    useState([]);

  const [pricingLoading, setPricingLoading] =
    useState(false);

  /*
  |--------------------------------------------------------------------------
  | FETCH PRICING PLANS WHEN OFFERING CHANGES
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const offeringId =
      value?.offeringId;

    if (!offeringId) {
      setPricingPlans([]);
      return;
    }

    if (
      typeof fetchPricingPlans !==
      "function"
    ) {
      console.error(
        "fetchPricingPlans was not provided to OfferItemForm"
      );

      return;
    }

    let mounted = true;

    const loadPricingPlans = async () => {
      try {
        setPricingLoading(true);

        const plans =
          await fetchPricingPlans(
            offeringId,
            true
          );

        if (!mounted) {
          return;
        }

        const normalizedPlans =
          (
            Array.isArray(plans)
              ? plans
              : plans?.data || []
          )
            .map((plan) => {
              const id =
                plan?.id ??
                plan?._id ??
                plan?.pricingPlanId ??
                plan?.pricing_plan_id;

              const unitPrice =
                plan?.unitPrice ??
                plan?.unit_price ??
                plan?.price ??
                plan?.amount ??
                0;

              return {
                value: id
                  ? String(id)
                  : "",
                label:
                  plan?.name ||
                  plan?.title ||
                  plan?.code ||
                  `Plan ${id || ""}`,
                unitPrice,
              };
            })
            .filter(
              (plan) => plan.value
            );

        setPricingPlans(
          normalizedPlans
        );
      } catch (error) {
        console.error(
          "Failed to fetch pricing plans:",
          error
        );

        if (mounted) {
          setPricingPlans([]);
        }
      } finally {
        if (mounted) {
          setPricingLoading(false);
        }
      }
    };

    loadPricingPlans();

    return () => {
      mounted = false;
    };
  }, [
    value?.offeringId,
    fetchPricingPlans,
  ]);

  /*
  |--------------------------------------------------------------------------
  | CHANGE
  |--------------------------------------------------------------------------
  */

  const handleChange = (event) => {
    const {
      name,
      value: inputValue,
    } = event.target;

    /*
    |--------------------------------------------------------------------------
    | OFFERING CHANGED
    |--------------------------------------------------------------------------
    */

    if (name === "offeringId") {
      onChange(index, {
        ...value,

        offeringId:
          inputValue,

        pricingPlanId: "",

        unitPrice: "",
      });

      return;
    }

    /*
    |--------------------------------------------------------------------------
    | PRICING PLAN CHANGED
    |--------------------------------------------------------------------------
    */

    if (
      name === "pricingPlanId"
    ) {
      const selectedPlan =
        pricingPlans.find(
          (plan) =>
            String(plan.value) ===
            String(inputValue)
        );

      onChange(index, {
        ...value,

        pricingPlanId:
          inputValue,

        unitPrice:
          selectedPlan?.unitPrice ??
          "",
      });

      return;
    }

    /*
    |--------------------------------------------------------------------------
    | OTHER FIELDS
    |--------------------------------------------------------------------------
    */

    onChange(index, {
      ...value,
      [name]: inputValue,
    });
  };

  /*
  |--------------------------------------------------------------------------
  | TOTAL
  |--------------------------------------------------------------------------
  */

  const calculated =
    calculateItemTotal({
      quantity:
        value.quantity || 1,

      unitPrice:
        value.unitPrice || 0,

      discountAmount:
        value.discountAmount || 0,

      taxAmount:
        value.taxAmount || 0,
    });

  return (
    <div className="offer-item-form">
      <div className="offer-item-form__header">
        <h3>
          Item {index + 1}
        </h3>

        {canRemove && (
          <Button
            type="button"
            variant="danger"
            size="small"
            onClick={() =>
              onRemove(index)
            }
          >
            Remove
          </Button>
        )}
      </div>

      <div className="offer-item-form__grid">
        {/* OFFERING */}

        <Select
          label="Offering"
          name="offeringId"
          value={String(
            value.offeringId || ""
          )}
          onChange={handleChange}
          options={offeringOptions}
          placeholder="Select offering"
          error={
            errors[
              `items.${index}.offeringId`
            ]
          }
          required
        />

        {/* PRICING PLAN */}

        <Select
          label="Pricing Plan"
          name="pricingPlanId"
          value={String(
            value.pricingPlanId || ""
          )}
          onChange={handleChange}
          options={pricingPlans}
          placeholder={
            pricingLoading
              ? "Loading pricing plans..."
              : !value.offeringId
              ? "Select offering first"
              : pricingPlans.length === 0
              ? "No pricing plans"
              : "Select pricing plan"
          }
          disabled={
            !value.offeringId ||
            pricingLoading
          }
          error={
            errors[
              `items.${index}.pricingPlanId`
            ]
          }
          required
        />

        {/* DESCRIPTION */}

        <Input
          label="Description"
          name="description"
          value={
            value.description || ""
          }
          onChange={handleChange}
          error={
            errors[
              `items.${index}.description`
            ]
          }
          required
        />

        {/* QUANTITY */}

        <Input
          label="Quantity"
          type="number"
          min="0.01"
          step="0.01"
          name="quantity"
          value={
            value.quantity ?? 1
          }
          onChange={handleChange}
          error={
            errors[
              `items.${index}.quantity`
            ]
          }
        />

        {/* UNIT PRICE */}

        <Input
          label="Unit Price"
          type="number"
          min="0"
          step="0.01"
          name="unitPrice"
          value={
            value.unitPrice ?? ""
          }
          onChange={handleChange}
          error={
            errors[
              `items.${index}.unitPrice`
            ]
          }
        />

        {/* DISCOUNT */}

        <Input
          label="Discount"
          type="number"
          min="0"
          step="0.01"
          name="discountAmount"
          value={
            value.discountAmount ?? 0
          }
          onChange={handleChange}
          error={
            errors[
              `items.${index}.discountAmount`
            ]
          }
        />

        {/* TAX */}

        <Input
          label="Tax"
          type="number"
          min="0"
          step="0.01"
          name="taxAmount"
          value={
            value.taxAmount ?? 0
          }
          onChange={handleChange}
          error={
            errors[
              `items.${index}.taxAmount`
            ]
          }
        />
      </div>

      <div className="offer-item-form__total">
        <span>
          Item Total
        </span>

        <strong>
          {Number(
            calculated.totalAmount || 0
          ).toFixed(2)}
        </strong>
      </div>
    </div>
  );
};

export default OfferItemForm;
